import { throttle } from 'lodash-es'
import { useCallback, useEffect } from 'react'
import {
    resetCenteredIndexState,
    setCenteredIndexState,
} from '../../../app-state/state.grid'
import { AppStateType, ListType, SettingsType } from '../../../constants/enums'
import {
    subscribeWorkerListUpdates,
    unsubscribeWorkerListUpdates,
} from '../../../state/pubSub/workerSubscriptions'
import { useAppState } from '../../Hooks/useAppState'
import { useList } from '../../Hooks/useList'
import { usePassive } from '../../Hooks/usePassive'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { useSettings } from '../../Hooks/useSettings'
import APP_BREAKPOINTS from '../../styles/breakpoints'

export const useHomeData = () => {
    const models = useList(ListType.DEFAULT, { passive: false })
    const noModelsVisible = models.length === 0
    const { isIOS, isIpad, isMobile } = useAppState(AppStateType.APP)

    const [settings] = useSettings(SettingsType.DEFAULT)
    const {
        pinRecentlyVisitedRooms,
        pinModelsWithNotes,
        pinTrendingRooms,
        pinPopularRooms,
        pinNewModels,
        pinMissMFC,
        gridDensity,
        display,
        // pinPrivateGroupShows,
        // pinModelsYouMayLike,
        // pinCustomSelection,
        // pinPastWinners,
    } = settings
    const passiveMode = usePassive()
    const windowWidth = useScreenStateSelector('windowWidth')
    const windowHeight = useScreenStateSelector('windowHeight')
    const hasPinnedRooms = useScreenStateSelector('hasPinnedRooms')
    const pinLimitReached = useScreenStateSelector('pinLimitReached')
    const singlePin = useScreenStateSelector('singlePin')

    const isMobileDevice =
        (isMobile || isIOS || isIpad) &&
        !(
            windowWidth > APP_BREAKPOINTS.lg / 2 &&
            windowHeight > APP_BREAKPOINTS.lg / 2
        )

    useEffect(() => {
        if (pinTrendingRooms === 1) {
            subscribeWorkerListUpdates(ListType.PINNED_MODEL_TRENDING)
        } else {
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_TRENDING)
        }
    }, [pinTrendingRooms])

    useEffect(() => {
        if (pinPopularRooms === 1) {
            subscribeWorkerListUpdates(ListType.PINNED_MODEL_POPULAR)
        } else {
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_POPULAR)
        }
    }, [pinPopularRooms])

    useEffect(() => {
        if (pinRecentlyVisitedRooms === 1) {
            subscribeWorkerListUpdates(ListType.PINNED_MODEL_RECENTLY_VISITED)
        } else {
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_RECENTLY_VISITED)
        }
    }, [pinRecentlyVisitedRooms])

    useEffect(() => {
        if (pinMissMFC === 1) {
            subscribeWorkerListUpdates(ListType.PINNED_MODEL_MISS_MFC)
        } else {
            unsubscribeWorkerListUpdates(ListType.PINNED_MODEL_MISS_MFC)
        }
    }, [pinMissMFC])

    const getCenteredIndex = useCallback(
        throttle(
            ({
                averageRowHeight,
                totalRowsHeight,
                containerHeight,
                scrollOffset,
            }) => {
                const index = Math.ceil(scrollOffset / averageRowHeight)

                if (totalRowsHeight < scrollOffset + containerHeight + 5) {
                    setCenteredIndexState(index + 1)
                    return
                }

                setCenteredIndexState(index)
            },
            120,
        ),
        [],
    )

    useEffect(() => {
        return () => {
            getCenteredIndex.cancel()
        }
    }, [getCenteredIndex])

    const enableCenteredIndexTracking =
        gridDensity === 3 &&
        (display === 'detailed' || display === 'preview') &&
        isMobileDevice &&
        !hasPinnedRooms

    useEffect(() => {
        resetCenteredIndexState()
    }, [enableCenteredIndexTracking])

    const enablePinToTopSymbols =
        pinRecentlyVisitedRooms === 1 ||
        pinTrendingRooms === 1 ||
        pinPopularRooms === 1 ||
        pinNewModels === 1 ||
        pinMissMFC === 1

    return {
        // Not implemented yet
        // showYouMayLike: boolean = pinModelsYouMayLike === 1,
        // Shows up automatically:
        // showCrownBadge: boolean = pinPastWinners === 1,
        enableOnScroll: enableCenteredIndexTracking,
        displayNoteIconButton: pinModelsWithNotes === 1,
        displayRecentlyVisitedHighlight: pinRecentlyVisitedRooms === 1,
        displayTrendingHighlight: pinTrendingRooms === 1,
        displayPopularHighlight: pinPopularRooms === 1,
        displayRecentlyVisitedBadge: pinRecentlyVisitedRooms === 1,
        displayTrendingBadge: pinTrendingRooms === 1,
        displayPopularBadge: pinPopularRooms === 1,
        displayNewModelBadge: pinNewModels === 1,
        displayMissMFCBadge: pinMissMFC === 1,
        // roomHeight: estimatedRoomHeight,
        // roomWidth: estimatedRoomWidth,
        enableCenteredIndexTracking,
        onScroll: getCenteredIndex,
        isMobile: isMobileDevice,
        enablePinToTopSymbols,
        // windowWidth: width,
        // showPlaceholder,
        noModelsVisible,
        pinLimitReached,
        // pinnedRooms,
        passiveMode,
        singlePin,
        // showChat,
        // vertical,
        // leftPin,
        models,
    }
}
